import React, { useCallback } from 'react';

import { IChatInputMessage } from '@fly/shared';
import { FlyRightPane } from '@fly/shared-ui';
import { selectLastHumanMessage, useAppSelector } from '@fly/store';
import { Separator } from '@radix-ui/themes';
import classNames from 'classnames';

import { useChatStream } from '../../hooks/useChatStream';
import styles from './FlyChat.module.less';
import {
  FlyChatContent,
  FlyChatError,
  FlyChatFooter,
  FlyChatHeader,
} from './components';

export const FlyChat: React.FC = React.memo(() => {
  const {
    messageIds,
    suggestions,

    isStreaming,
    isThreadLoading,
    queuedMessages,

    streamError: errorMessage,
    sendMessage: onSendMessage,
    enqueueMessage: onEnqueueMessage,
    dequeueMessage: onDequeueMessage,
    removeQueuedMessage: onRemoveQueuedMessage,
    clearQueuedMessages: onClearQueuedMessages,
    closeChat: onClose,
    newChat: onNewChat,
    deleteThread: onDeleteThread,
    selectThread,
    currentThread,
    threadTitle,
  } = useChatStream();

  const lastHumanMessage = useAppSelector((state) =>
    selectLastHumanMessage(state, currentThread?.thread_id || '')
  ) as IChatInputMessage | null;

  const handleRetry = useCallback(() => {
    if (lastHumanMessage) {
      onSendMessage(lastHumanMessage.content);
    } else {
      onNewChat();
    }
  }, [lastHumanMessage, onSendMessage, onNewChat]);

  return (
    <div className={styles.drawerContainer} data-testid='chat-container'>
      <FlyRightPane data-testid='chat-right-pane'>
        <div className={styles.chatHeader}>
          <FlyChatHeader
            title={threadTitle}
            isStreaming={isStreaming}
            isThreadLoading={isThreadLoading}
            isChatEmpty={messageIds.length === 0}
            currentThreadId={currentThread?.thread_id}
            onNewChat={onNewChat}
            onClose={onClose}
            onSelectThread={selectThread}
            onDeleteThread={onDeleteThread}
          />
          <Separator size='1' className='w-full' />
        </div>
        <div className={styles.chatContent}>
          {messageIds.length > 0 && (
            <FlyChatContent
              onRetry={handleRetry}
              errorMessage={errorMessage}
              isStreaming={isStreaming}
              messageIds={messageIds}
              threadId={currentThread?.thread_id}
            />
          )}
          {!messageIds.length && (
            <div className={styles.emptyState}>
              {errorMessage && (
                <div className={classNames(styles.error, 'p-fly-4')}>
                  <FlyChatError onRetry={handleRetry} />
                </div>
              )}
            </div>
          )}
        </div>
        <div className={classNames(styles.chatFooter, 'relative')}>
          <FlyChatFooter
            suggestions={suggestions}
            onSendMessage={onSendMessage}
            isLoading={isThreadLoading}
            isStreaming={isStreaming}
            streamError={errorMessage}
            queuedMessages={queuedMessages}
            onEnqueueMessage={onEnqueueMessage}
            onDequeueMessage={onDequeueMessage}
            onRemoveQueuedMessage={onRemoveQueuedMessage}
            onClearQueuedMessages={onClearQueuedMessages}
          />
        </div>
      </FlyRightPane>
    </div>
  );
});

FlyChat.displayName = 'FlyChat';
